import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Loader2,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  User,
  Users,
  ImageIcon,
  FileText,
} from "lucide-react";
import { showToastNotification } from "@/utility/helper";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import { Button } from "@/components/ui/button";
import { RoutesLink } from "@/utility/RouteLinks";

// ─── Types ──────────────────────────────────────────────────────────
interface ISupervisorDetail {
  employeeCode: string;
  fullName: string;
  gender: string;
  email: string;
  primaryPhone: string;
  secondaryPhone?: string;
  site: string;
  department: string;
  shift: string;
  dailyRate: string;
  joiningDate: string;
  status: "active" | "inactive";
  emergencyContactPerson: string;
  emergencyContactPhone: string;
  profileImageUrl?: string | null;
  aadharPhotoUrl?: string | null;
  documentUrl?: string | null;
}

// ─── Reusable detail field ────────────────────────────────────────
const DetailField = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number | undefined;
}) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0 mt-0.5">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">
        {value || "—"}
      </p>
    </div>
  </div>
);

// ─── Section wrapper (matching CreateLabor) ──────────────────────
const DetailSection = ({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="border-b border-gray-100 last:border-b-0 px-6 py-7 sm:px-8">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-[13px] text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);

// ─── Main Component ───────────────────────────────────────────────
const SupervisorDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [supervisor, setSupervisor] = useState<ISupervisorDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSupervisor = async () => {
      setIsLoading(true);
      try {
        // TODO: Replace with actual API call
        // const response = await SupervisorService.getById(id);
        // if (response?.status) setSupervisor(response.data);

        // Mock data with all fields
        const mockData: ISupervisorDetail = {
          employeeCode: "EMP-001",
          fullName: "Rahul Singh",
          gender: "Male",
          email: "rahul@example.com",
          primaryPhone: "9876543210",
          secondaryPhone: "9876543211",
          site: "Site 1 — Foundation",
          department: "Civil",
          shift: "Day Shift (08:00 – 17:00)",
          dailyRate: "₹750",
          joiningDate: "12 Jan 2025",
          status: "active",
          emergencyContactPerson: "Sunita Devi",
          emergencyContactPhone: "9876543212",
          profileImageUrl: null,
          aadharPhotoUrl: null,
          documentUrl: null,
        };
        await new Promise((resolve) => setTimeout(resolve, 300));
        setSupervisor(mockData);
      } catch (error) {
        showToastNotification("danger", "Failed to load supervisor details");
        navigate(RoutesLink.supervisors);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSupervisor();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!supervisor) return null;

  const initials = supervisor.fullName
    .split(" ")
    .map((p) => p.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // Helper to render file link if URL exists
  const renderFileLink = (label: string, url?: string | null) => {
    if (!url)
      return <span className="text-sm text-gray-400">Not uploaded</span>;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary hover:underline flex items-center gap-1"
      >
        <FileText size={14} />
        View {label}
      </a>
    );
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Header with back button and edit button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 w-9 p-0 hover:bg-muted shrink-0"
            onClick={() => navigate(RoutesLink.supervisors)}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <MainHeading title="Supervisor Details" />
            <p className="text-[13px] text-gray-400 -mt-0.5">
              View complete profile and assignment
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`${RoutesLink.editSupervisor}/${id}`)}
          className="gap-2"
        >
          <Pencil className="h-4 w-4" /> Edit
        </Button>
      </div>

      {/* Main card */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        {/* Profile header with avatar and status */}
        <div className="flex items-center gap-4 px-6 py-5 sm:px-8 border-b border-gray-100">
          <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg shrink-0">
            {supervisor.profileImageUrl ? (
              <img
                src={supervisor.profileImageUrl}
                alt={supervisor.fullName}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold text-gray-900 truncate">
              {supervisor.fullName}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full ${
                  supervisor.status === "active"
                    ? "text-green-600 border-green-600 bg-green-50"
                    : "text-red-600 border-red-600 bg-red-50"
                }`}
              >
                {supervisor.status.charAt(0).toUpperCase() +
                  supervisor.status.slice(1)}
              </span>
              <span className="text-xs text-gray-400">
                {supervisor.employeeCode}
              </span>
            </div>
          </div>
        </div>

        {/* ─── Basic Information ────────────────────────────── */}
        <DetailSection icon={<User size={17} />} title="Basic Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <DetailField
              icon={<User size={15} />}
              label="Employee Code"
              value={supervisor.employeeCode}
            />
            <DetailField
              icon={<User size={15} />}
              label="Full Name"
              value={supervisor.fullName}
            />
            <DetailField
              icon={<User size={15} />}
              label="Gender"
              value={supervisor.gender}
            />
            <DetailField
              icon={<Mail size={15} />}
              label="Email"
              value={supervisor.email}
            />
            <DetailField
              icon={<Phone size={15} />}
              label="Primary Phone"
              value={supervisor.primaryPhone}
            />
            <DetailField
              icon={<Phone size={15} />}
              label="Secondary Phone"
              value={supervisor.secondaryPhone || "—"}
            />
          </div>
        </DetailSection>

        {/* ─── Work Details ──────────────────────────────────── */}
        <DetailSection
          icon={<Briefcase size={17} />}
          title="Work Details"
          description="Site, shift, and compensation"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <DetailField
              icon={<MapPin size={15} />}
              label="Site"
              value={supervisor.site}
            />
            <DetailField
              icon={<Briefcase size={15} />}
              label="Department"
              value={supervisor.department}
            />
            <DetailField
              icon={<Briefcase size={15} />}
              label="Shift"
              value={supervisor.shift}
            />
            <DetailField
              icon={<Briefcase size={15} />}
              label="Daily Rate"
              value={supervisor.dailyRate}
            />
            <DetailField
              icon={<Calendar size={15} />}
              label="Joining Date"
              value={supervisor.joiningDate}
            />
          </div>
        </DetailSection>

        {/* ─── Emergency Contact ────────────────────────────── */}
        <DetailSection
          icon={<Users size={17} />}
          title="Emergency Contact"
          description="Who to reach in case of an incident"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <DetailField
              icon={<Users size={15} />}
              label="Contact Person"
              value={supervisor.emergencyContactPerson}
            />
            <DetailField
              icon={<Phone size={15} />}
              label="Contact Number"
              value={supervisor.emergencyContactPhone}
            />
          </div>
        </DetailSection>

        {/* ─── Documents (optional) ─────────────────────────── */}
        {(supervisor.profileImageUrl ||
          supervisor.aadharPhotoUrl ||
          supervisor.documentUrl) && (
          <DetailSection
            icon={<ImageIcon size={17} />}
            title="Documents"
            description="Uploaded files"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <p className="text-xs text-gray-400">Profile Image</p>
                {renderFileLink("Profile", supervisor.profileImageUrl)}
              </div>
              <div>
                <p className="text-xs text-gray-400">Aadhar Photo</p>
                {renderFileLink("Aadhar", supervisor.aadharPhotoUrl)}
              </div>
              <div>
                <p className="text-xs text-gray-400">Document</p>
                {renderFileLink("Document", supervisor.documentUrl)}
              </div>
            </div>
          </DetailSection>
        )}
      </div>
    </div>
  );
};

export default SupervisorDetails;
